import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { wfirmaAccountService } from '../services/wfirmaAccountService';
import { companyService } from '../services/companyService';
import { useTranslation } from '../hooks/useTranslation';
import { WFirmaCompanyAccount } from '../types/wfirmaCompanyAccount';
import { BankAccount } from '../types/company';

interface WFirmaAccountSyncProps {
  companyId: string;
}

const WFirmaAccountSync: React.FC<WFirmaAccountSyncProps> = ({ companyId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [mappings, setMappings] = useState<Map<string, string>>(new Map()); // bankAccountId -> wfirmaAccountId

  // Загружаем счета wFirma
  const { data: wfirmaAccounts = [], isLoading: loadingWFirma, refetch: refetchWFirma } = useQuery({
    queryKey: ['wfirma-accounts', companyId],
    queryFn: () => wfirmaAccountService.getCompanyAccounts(companyId),
    enabled: !!companyId
  });

  // Загружаем счета компании
  const { data: company, isLoading: loadingCompany } = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => companyService.getCompany(companyId),
    enabled: !!companyId
  });

  // Загружаем связи при загрузке данных
  const { data: mappingsData, refetch: refetchMappings } = useQuery({
    queryKey: ['wfirma-account-mappings', companyId],
    queryFn: () => wfirmaAccountService.getMappings(companyId),
    enabled: !!companyId && !!company?.bankAccounts && company.bankAccounts.length > 0
  });

  useEffect(() => {
    if (mappingsData) {
      const newMappings = new Map<string, string>();
      Object.entries(mappingsData).forEach(([bankAccountId, wfirmaAccountId]) => {
        newMappings.set(bankAccountId, wfirmaAccountId);
      });
      setMappings(newMappings);
    }
  }, [mappingsData]);

  // Синхронизация счетов
  const syncMutation = useMutation({
    mutationFn: () => wfirmaAccountService.syncCompanyAccounts(companyId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wfirma-accounts', companyId] });
      alert(t('wfirma.accounts.syncSuccess') || 'Счета успешно синхронизированы');
    },
    onError: (error: any) => {
      alert(error?.response?.data?.message || error?.message || t('wfirma.accounts.syncError') || 'Ошибка синхронизации счетов');
    }
  });

  const clearLocalMutation = useMutation({
    mutationFn: () => wfirmaAccountService.clearLocalWfirmaAccounts(companyId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['wfirma-accounts', companyId] });
      queryClient.invalidateQueries({ queryKey: ['wfirma-account-mappings', companyId] });
      queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      alert(
        (t('wfirma.accounts.clearLocalSuccess') || 'Удалено записей') + `: ${data.deletedCount}`
      );
    },
    onError: (error: any) => {
      alert(
        error?.response?.data?.message ||
          error?.message ||
          t('wfirma.accounts.clearLocalError') ||
          'Ошибка удаления локальных счетов wFirma'
      );
    }
  });

  // Автоматическая связь
  const autoMapMutation = useMutation({
    mutationFn: () => wfirmaAccountService.autoMapAccounts(companyId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      queryClient.invalidateQueries({ queryKey: ['wfirma-account-mappings', companyId] });
      alert(`${t('wfirma.accounts.autoMapSuccess') || 'Автоматически связано счетов'}: ${data.mappedCount}`);
    },
    onError: (error: any) => {
      alert(error?.response?.data?.message || error?.message || t('wfirma.accounts.autoMapError') || 'Ошибка автоматической связи');
    }
  });

  // Ручная связь
  const mapMutation = useMutation({
    mutationFn: ({ bankAccountId, wfirmaAccountId }: { bankAccountId: string; wfirmaAccountId: string }) =>
      wfirmaAccountService.mapAccount(companyId, bankAccountId, wfirmaAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      queryClient.invalidateQueries({ queryKey: ['wfirma-account-mappings', companyId] });
    },
    onError: (error: any) => {
      alert(error?.response?.data?.message || error?.message || t('wfirma.accounts.mapError') || 'Ошибка связи счетов');
    }
  });

  // Удаление связи
  const unmapMutation = useMutation({
    mutationFn: (bankAccountId: string) => wfirmaAccountService.unmapAccount(companyId, bankAccountId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', companyId] });
      queryClient.invalidateQueries({ queryKey: ['wfirma-account-mappings', companyId] });
    },
    onError: (error: any) => {
      alert(error?.response?.data?.message || error?.message || t('wfirma.accounts.unmapError') || 'Ошибка удаления связи');
    }
  });

  const handleMapAccount = (bankAccountId: string, wfirmaAccountId: string) => {
    mapMutation.mutate({ bankAccountId, wfirmaAccountId });
  };

  const handleUnmapAccount = (bankAccountId: string) => {
    if (window.confirm(t('wfirma.accounts.unmapConfirm') || 'Вы уверены, что хотите удалить связь?')) {
      unmapMutation.mutate(bankAccountId);
    }
  };

  const handleClearLocalWfirmaAccounts = () => {
    if (
      window.confirm(
        t('wfirma.accounts.clearLocalConfirm') ||
          'Удалить все локально сохранённые счета wFirma и связи с банковскими счетами? В самой wFirma счета не удаляются. После этого нажмите «Синхронизировать счета».'
      )
    ) {
      clearLocalMutation.mutate();
    }
  };

  // Получаем bankAccountId по wfirmaAccountId (обратный маппинг)
  const getBankAccountIdForWFirmaAccount = (wfirmaAccountId: string): string | undefined => {
    const bankAccountIds = Array.from(mappings.keys());
    for (const bankAccountId of bankAccountIds) {
      const mappedWFirmaId = mappings.get(bankAccountId);
      if (mappedWFirmaId === wfirmaAccountId) {
        return bankAccountId;
      }
    }
    return undefined;
  };

  if (loadingWFirma || loadingCompany) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
        {/* Кнопки действий */}
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {syncMutation.isPending
              ? (t('common.syncing') || 'Синхронизация...')
              : (t('wfirma.accounts.sync') || 'Синхронизировать счета')}
          </button>
          
          <button
            onClick={() => autoMapMutation.mutate()}
            disabled={autoMapMutation.isPending || wfirmaAccounts.length === 0}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {autoMapMutation.isPending
              ? (t('common.processing') || 'Обработка...')
              : (t('wfirma.accounts.autoMap') || 'Автоматически связать')}
          </button>

          <button
            type="button"
            onClick={handleClearLocalWfirmaAccounts}
            disabled={clearLocalMutation.isPending}
            className="px-4 py-2 border border-red-300 text-red-700 bg-white rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {clearLocalMutation.isPending
              ? (t('common.loading') || '...')
              : (t('wfirma.accounts.clearLocal') || 'Удалить локальные счета wFirma')}
          </button>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {t('wfirma.accounts.clearLocalHint') ||
            'После смены аккаунта wFirma удалите локальный кэш, затем синхронизируйте и при необходимости свяжите счета заново.'}
        </p>

        {/* Список счетов wFirma */}
        <div>
          <h4 className="text-lg font-semibold text-gray-800 mb-3">
            {t('wfirma.accounts.wfirmaAccounts') || 'Счета в wFirma'} ({wfirmaAccounts.length})
          </h4>
          {wfirmaAccounts.length === 0 ? (
            <p className="text-sm text-gray-500">
              {t('wfirma.accounts.noWFirmaAccounts') || 'Нет синхронизированных счетов. Нажмите "Синхронизировать счета" для загрузки.'}
            </p>
          ) : (
            <div className="space-y-3">
              {wfirmaAccounts.map((account) => {
                // Проверяем, связан ли этот счет wFirma с каким-либо внутренним счетом
                const linkedBankAccountId = getBankAccountIdForWFirmaAccount(account.id);
                const isLinked = !!linkedBankAccountId;
                const linkedBankAccount = linkedBankAccountId 
                  ? company?.bankAccounts?.find(ba => ba.id === linkedBankAccountId)
                  : undefined;
                
                return (
                  <div
                    key={account.id}
                    className="p-4 border border-gray-200 rounded-lg"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-gray-900">
                            {account.name || (account.number && account.number !== '-' ? account.number : `ID: ${account.wfirmaId}`)}
                          </p>
                          <span 
                            className={`px-2 py-1 text-xs font-medium rounded-full ${
                              isLinked 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {isLinked 
                              ? (t('wfirma.accounts.synchronized') || 'Синхронизирован') 
                              : (t('wfirma.accounts.notSynchronized') || 'Нет')}
                          </span>
                        </div>
                        {account.currency && (
                          <p className="text-sm text-gray-600">
                            {account.currency}
                          </p>
                        )}
                      </div>
                      {isLinked && linkedBankAccountId && (
                        <button
                          onClick={() => handleUnmapAccount(linkedBankAccountId)}
                          disabled={unmapMutation.isPending}
                          className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors disabled:opacity-50"
                        >
                          {t('wfirma.accounts.unmap') || 'Удалить связь'}
                        </button>
                      )}
                    </div>
                    
                    {isLinked && linkedBankAccount ? (
                      <div className="p-2 bg-green-50 border border-green-200 rounded">
                        <p className="text-sm text-green-700">
                          <span className="font-medium">{t('wfirma.accounts.mappedTo') || 'Связано с'}:</span>{' '}
                          {linkedBankAccount.name} - {linkedBankAccount.accountNumber} ({linkedBankAccount.currency})
                        </p>
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          {t('wfirma.accounts.selectLocalAccount') || 'Выберите местный счет:'}
                        </label>
                        <select
                          value=""
                          onChange={(e) => {
                            if (e.target.value) {
                              handleMapAccount(e.target.value, account.id);
                            }
                          }}
                          disabled={mapMutation.isPending || !company?.bankAccounts || company.bankAccounts.length === 0}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50"
                        >
                          <option value="">
                            {t('wfirma.accounts.selectAccount') || '-- Выберите счет --'}
                          </option>
                          {company?.bankAccounts
                            ?.filter(ba => ba.currency === account.currency)
                            .map((bankAccount) => (
                              <option key={bankAccount.id} value={bankAccount.id}>
                                {bankAccount.name} - {bankAccount.accountNumber} ({bankAccount.currency})
                              </option>
                            ))}
                        </select>
                        {(!company?.bankAccounts || company.bankAccounts.filter(ba => ba.currency === account.currency).length === 0) && (
                          <p className="mt-1 text-xs text-gray-500">
                            {t('wfirma.accounts.noMatchingCurrency') || 'Нет местных счетов с валютой'} {account.currency}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
    </div>
  );
};

export default WFirmaAccountSync;
